// Admin page, street section: approvals, houses (in street order), each
// house's Pi keys, settings and residents, and the street's own settings.
import type { HouseMode, HouseStyle } from "../shared/protocol.ts";
import { $, api, el, helpButton, openDialog, toast } from "./site.ts";

interface AdminSession {
  id: string;
  role: "admin" | "resident";
  name: string;
  device: string;
  status: "pending" | "approved";
  houseId: string | null;
  address: string | null;
  createdAt: number;
  lastSeenAt: number;
}

interface AdminHouse {
  id: string;
  address: string;
  position: number;
  mode: HouseMode;
  offsetMs: number;
  style: HouseStyle;
  online: boolean;
  lastSeen: number | null;
  agentVersion: string | null;
  hasPi: boolean;
  joined: boolean;
  admin: boolean;
  login: { hasPassword: boolean; passwordSetAt: number | null; codeActive: boolean; codeExpiresAt: number | null };
  strands: AdminStrand[];
  keys: { id: string; label: string; createdAt: number; lastUsedAt: number | null }[];
  residents: AdminSession[];
}

interface AdminStrand {
  id: string;
  sku: string;
  name: string;
  order: number;
  realtime: boolean;
  segments: number | null;
  reverse: boolean;
  inShow: boolean;
  seenRecently: boolean;
  ip: string | null;
}

interface StreetState {
  name: string;
  publicUrl: string;
  season: "christmas" | "halloween";
  houses: AdminHouse[];
}

const MODE_LABELS: Record<HouseMode, string> = { street: "Admin control", own: "My own", off: "Off" };

let state: StreetState | null = null;
let refreshQueued = false;

function when(ms: number | null): string {
  if (!ms) return "never";
  const min = Math.round((Date.now() - ms) / 60000);
  if (min < 2) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  return h < 48 ? `${h} h ago` : new Date(ms).toLocaleDateString();
}

async function act(method: string, url: string, body: unknown, message: string): Promise<boolean> {
  try {
    await api(method, url, body);
    toast(message);
    await refresh(true);
    return true;
  } catch (e) {
    toast((e as Error).message);
    return false;
  }
}

export async function refresh(force = false): Promise<void> {
  // Don't redraw under someone who is typing in the houses list.
  const active = document.activeElement;
  if (!force && active && $("#street-admin").contains(active) && active.matches("input, select, textarea")) return;
  try {
    state = await api<StreetState>("GET", "/api/admin/street");
  } catch (e) {
    toast((e as Error).message);
    return;
  }
  render();
}

// Called when the hub reports a house change over the live connection.
export function refreshSoon(): void {
  if (refreshQueued) return;
  refreshQueued = true;
  setTimeout(() => {
    refreshQueued = false;
    void refresh();
  }, 800);
}

function render(): void {
  if (!state) return;
  $("#street-name-input").setAttribute("placeholder", state.name);
  $<HTMLInputElement>("#street-name-input").value ||= state.name;
  const urlInput = $<HTMLInputElement>("#street-url-input");
  if (document.activeElement !== urlInput) urlInput.value = state.publicUrl;
  const seasonInput = $<HTMLSelectElement>("#street-season-input");
  if (document.activeElement !== seasonInput) seasonInput.value = state.season;
  $("#admin-houses").replaceChildren(...state.houses.map(renderHouse));
  $("#no-houses-yet").hidden = state.houses.length > 0;
}

function renderHouse(h: AdminHouse, index: number): HTMLElement {
  const base = `/api/admin/houses/${h.id}`;
  const status = h.online
    ? el("span", { class: "pill ok" }, el("span", { class: "dot ok" }), "Pi online")
    : el("span", { class: h.hasPi ? "pill warn" : "pill" }, h.hasPi ? `Pi offline · seen ${when(h.lastSeen)}` : "No Pi yet");

  // Order along the street.
  const up = el("button", { type: "button", "aria-label": "Move up the street" }, "↑");
  const down = el("button", { type: "button", "aria-label": "Move down the street" }, "↓");
  up.toggleAttribute("disabled", index === 0);
  down.toggleAttribute("disabled", index === (state?.houses.length ?? 0) - 1);
  const swap = async (other: AdminHouse | undefined) => {
    if (!other) return;
    await api("PATCH", base, { position: other.position });
    await act("PATCH", `/api/admin/houses/${other.id}`, { position: h.position }, "Order updated");
  };
  up.addEventListener("click", () => void swap(state?.houses[index - 1]));
  down.addEventListener("click", () => void swap(state?.houses[index + 1]));

  // Technical settings the admin can always tune.
  const realtime = el("input", { type: "checkbox" });
  realtime.checked = h.style.realtime;
  realtime.addEventListener("change", () => void act("PATCH", base, { style: { realtime: realtime.checked } }, "Saved"));
  const flash = el("input", { type: "checkbox" });
  flash.checked = h.style.flash;
  flash.addEventListener("change", () => void act("PATCH", base, { style: { flash: flash.checked } }, "Saved"));
  const offset = el("input", { type: "number", min: "-2000", max: "2000", step: "10", value: String(h.offsetMs), "aria-label": "Timing offset in milliseconds" });
  offset.addEventListener("change", () => void act("PATCH", base, { offsetMs: Math.round(Number(offset.value) || 0) }, "Offset saved"));

  // Mode and lights: the household's choice once they've joined.
  const choices: HTMLElement[] = [];
  if (!h.joined) {
    const mode = el("select", { "aria-label": "Who runs the lights (until the household joins)" });
    for (const m of ["street", "own", "off"] as HouseMode[]) {
      const opt = el("option", { value: m }, MODE_LABELS[m]);
      if (m === h.mode) opt.selected = true;
      mode.append(opt);
    }
    mode.addEventListener("change", () => void act("PATCH", base, { mode: mode.value }, "Mode saved"));
    choices.push(el("label", {}, "Mode (until they join) ", mode));
  }
  const strands = h.strands.length
    ? el("ol", { class: "admin-strands" }, ...h.strands.map((s, i) => renderStrand(h, s, i)))
    : el("span", { class: "hint" }, "No lights found yet");

  // Login: password set, code waiting to be used, or nothing yet.
  const loginStatus = h.login.hasPassword
    ? el("span", { class: "pill ok" }, `Password set ${when(h.login.passwordSetAt)}`)
    : h.login.codeActive
      ? el("span", { class: "pill warn" }, `Code given, not used yet (expires ${new Date(h.login.codeExpiresAt!).toLocaleDateString()})`)
      : el("span", { class: "pill" }, "No login yet");
  const giveCode = el("button", { type: "button", class: "secondary" }, h.login.hasPassword || h.login.codeActive ? "New login code" : "Give login code");
  giveCode.addEventListener("click", () => void issueCode(h));
  const adminBox = el("input", { type: "checkbox" });
  adminBox.checked = h.admin;
  adminBox.addEventListener("change", () =>
    void act("PATCH", base, { admin: adminBox.checked }, adminBox.checked ? `House ${h.address} can use the admin console` : "Admin removed"),
  );
  const devices = h.residents.map((s) => {
    const signOut = el("button", { type: "button", class: "link-button" }, "Sign out");
    signOut.addEventListener("click", () => void act("DELETE", `/api/admin/sessions/${s.id}`, undefined, "Signed out"));
    return el("div", { class: "inline" }, `${s.device} · seen ${when(s.lastSeenAt)}`, signOut);
  });

  const keys = h.keys.map((k) => {
    const revoke = el("button", { type: "button", class: "link-button" }, "Revoke");
    revoke.addEventListener("click", () => {
      if (confirm(`Revoke ${k.label}? That Pi will stop working until you prepare it again.`)) {
        void act("DELETE", `${base}/keys/${k.id}`, undefined, "Key revoked");
      }
    });
    return el("div", { class: "inline" }, `${k.label} · last used ${when(k.lastUsedAt)}`, revoke);
  });
  const prepare = el("button", { type: "button", class: "secondary" }, h.keys.length ? "Prepare another Pi" : "Prepare a Pi");
  prepare.addEventListener("click", () => void preparePi(h));

  const remove = el("button", { type: "button", class: "link-button" }, "Remove house");
  remove.addEventListener("click", () => {
    if (confirm(`Remove ${h.address}? Its Pi keys and sign-ins are deleted.`)) void act("DELETE", base, undefined, "House removed");
  });

  return el(
    "article",
    { class: "admin-house" },
    el(
      "header",
      {},
      el("h3", {}, `${index + 1}. ${h.address}`),
      el("div", { class: "inline" }, status, el("span", { class: "pill" }, MODE_LABELS[h.mode]), up, down),
    ),
    el(
      "dl",
      { class: "kv" },
      el("dt", {}, "Strands"),
      el("dd", {}, strands),
      el("dt", {}, "Style"),
      el(
        "dd",
        {},
        el(
          "div",
          { class: "inline" },
          el("label", { title: "Instant color changes (works on the H6860). Sets every strand; single strands can then differ." }, realtime, "Real-time (all strands)"),
          el("label", { title: "Flash on each beat; for lights that fade (H705A, H70C9)" }, flash, "Flash"),
          el("label", {}, "Offset", offset, "ms"),
        ),
      ),
      ...(choices.length ? [el("dt", {}, "Mode"), el("dd", {}, ...choices)] : []),
      el("dt", {}, "Login"),
      el(
        "dd",
        {},
        el("div", { class: "inline" }, loginStatus, giveCode),
        el("label", { class: "inline", title: "This house's login also opens the admin console" }, adminBox, "Admin (e.g. your own house)"),
        ...devices,
      ),
      el("dt", {}, "Pi"),
      el("dd", {}, ...keys, el("div", { class: "inline" }, prepare, helpButton("admin-pi", "How to prepare a Pi"))),
    ),
    el("div", {}, remove),
  );
}

// One strand: name, model and order (the household's once they've joined),
// plus the technical switches the admin can always tune.
function renderStrand(h: AdminHouse, s: AdminStrand, index: number): HTMLElement {
  const base = `/api/admin/houses/${h.id}`;
  const change = (fields: Record<string, unknown>, message: string) =>
    void act("PATCH", base, { strands: [{ id: s.id, ...fields }] }, message);

  const inShow = el("input", { type: "checkbox", "aria-label": `${s.name} in the show` });
  inShow.checked = s.inShow;
  inShow.disabled = h.joined;
  inShow.addEventListener("change", () => {
    const ids = h.strands.filter((x) => (x.id === s.id ? inShow.checked : x.inShow)).map((x) => x.id);
    void act("PATCH", base, { allowedDevices: ids }, "Lights saved");
  });

  const title: (Node | string)[] = [inShow, `${index + 1}.`];
  if (h.joined) {
    title.push(el("strong", {}, s.name));
  } else {
    const name = el("input", { type: "text", maxlength: "40", "aria-label": "Strand name" });
    name.value = s.name;
    name.addEventListener("change", () => change({ name: name.value }, "Name saved"));
    const up = el("button", { type: "button", "aria-label": `Move ${s.name} earlier` }, "↑");
    const down = el("button", { type: "button", "aria-label": `Move ${s.name} later` }, "↓");
    up.toggleAttribute("disabled", index === 0);
    down.toggleAttribute("disabled", index === h.strands.length - 1);
    up.addEventListener("click", () => change({ order: s.order - 1 }, "Order saved"));
    down.addEventListener("click", () => change({ order: s.order + 1 }, "Order saved"));
    title.push(name, up, down);
  }

  const realtime = el("input", { type: "checkbox" });
  realtime.checked = s.realtime;
  realtime.addEventListener("change", () => change({ realtime: realtime.checked }, "Saved"));
  const reverse = el("input", { type: "checkbox" });
  reverse.checked = s.reverse;
  reverse.addEventListener("change", () => change({ reverse: reverse.checked }, "Saved"));
  const switches: HTMLElement[] = [
    el("label", { title: "Govee real-time mode for this strand" }, realtime, "Real-time"),
    el("label", { title: "Section 1 is at the far end" }, reverse, "Reverse"),
  ];
  if (s.realtime) {
    const segments = el("input", { type: "number", min: "1", max: "15", step: "1", placeholder: "15", "aria-label": "Sections" });
    segments.value = s.segments === null ? "" : String(s.segments);
    segments.addEventListener("change", () => {
      const n = Math.round(Number(segments.value));
      change({ segments: segments.value.trim() === "" ? null : Math.min(15, Math.max(1, n || 1)) }, "Sections saved");
    });
    switches.push(el("label", { title: "Sections in real-time mode (blank = 15)" }, "Sections", segments));
  }

  const model = `${s.sku}${s.ip ? ` @ ${s.ip}` : ""}${s.seenRecently ? "" : " · not seen lately"}`;
  return el(
    "li",
    {},
    el("div", { class: "strand-title" }, ...title),
    el("span", { class: "hint" }, model),
    el("div", { class: "inline" }, ...switches),
  );
}

// One-time login code: shown once, with a printable card to hand over.
async function issueCode(h: AdminHouse): Promise<void> {
  if ((h.login.hasPassword || h.login.codeActive) &&
    !confirm(`Give house ${h.address} a new code? Their current password and any unused code stop working, and they're signed out everywhere.`)) {
    return;
  }
  let issued: { username: string; code: string; expiresAt: number };
  try {
    issued = await api("POST", `/api/admin/houses/${h.id}/login-code`);
  } catch (e) {
    toast((e as Error).message);
    return;
  }
  const site = (state?.publicUrl ?? location.origin).replace(/^https?:\/\//, "");
  const print = el("button", { type: "button", class: "secondary" }, "Print a login card");
  print.addEventListener("click", () => {
    try {
      localStorage.setItem("cc-login-card", JSON.stringify({ ...issued, storedAt: Date.now() }));
    } catch {
      toast("Couldn't prepare the card in this browser; copy the code by hand.");
      return;
    }
    window.open("/neighborhood/signs", "_blank");
  });
  openDialog(
    `Login for house ${issued.username}`,
    el(
      "div",
      {},
      el("p", {}, "Give this to the household in person. It's shown only once."),
      el(
        "dl",
        { class: "kv", style: "font-size:18px" },
        el("dt", {}, "Website"),
        el("dd", {}, el("strong", {}, `${site}/neighborhood`)),
        el("dt", {}, "Username"),
        el("dd", {}, el("strong", {}, issued.username)),
        el("dt", {}, "One-time code"),
        el("dd", {}, el("strong", { class: "code", style: "font-size:20px;display:inline-block" }, issued.code)),
      ),
      el("p", { class: "hint" }, `They choose "First time?", enter both, and pick their own password. The code works once and expires ${new Date(issued.expiresAt).toLocaleDateString()}.`),
      el("div", { class: "actions", style: "justify-content:flex-start" }, print),
    ),
  );
  await refresh(true);
}

async function preparePi(h: AdminHouse): Promise<void> {
  let result: { key: string; setupCommand: string };
  try {
    result = await api("POST", `/api/admin/houses/${h.id}/keys`, { label: `Pi ${h.keys.length + 1}` });
  } catch (e) {
    toast((e as Error).message);
    return;
  }
  // The command carries no key: the setup script asks for it at a hidden
  // prompt, so the key never lands in the Pi's shell history (security.md S13).
  const command = result.setupCommand;
  const copyButton = (label: string, text: string) => {
    const button = el("button", { type: "button", class: "secondary" }, label);
    button.addEventListener("click", async () => {
      try {
        await navigator.clipboard.writeText(text);
        toast("Copied");
      } catch {
        toast("Select it and copy it");
      }
    });
    return button;
  };
  const row = "justify-content:flex-start;margin:8px 0 16px";
  openDialog(
    `Prepare a Pi for ${h.address}`,
    el(
      "div",
      {},
      el("p", {}, "1. On the Pi (over ssh), paste this command:"),
      el("div", { class: "code" }, command),
      el("div", { class: "actions", style: row }, copyButton("Copy command", command)),
      el("p", {}, "2. When it asks, paste this Pi's key. It stays hidden as you paste. The key is shown only once."),
      el("div", { class: "code" }, result.key),
      el("div", { class: "actions", style: row }, copyButton("Copy key", result.key)),
      el(
        "p",
        {},
        "3. It asks whether the Pi stays at this house. Press Enter if you'll take it to another house; " +
          "type y if it's already at its own house.",
      ),
      el(
        "ol",
        {},
        el("li", {}, "Set up the SD card with Raspberry Pi Imager (admin guide, Part D)."),
        el("li", {}, "Plug the Pi into your router and power; ssh ccadmin@cc-NAME.local"),
        el("li", {}, "Paste the command, then the key. In a minute this house shows Pi online."),
        el("li", {}, "Unplug it and take it over. It won't look for lights until it's plugged in there, and won't touch any until the household ticks theirs."),
      ),
    ),
  );
  await refresh(true);
}

export function setupStreetAdmin(): void {
  $("#street-admin").hidden = false;
  // A login card from earlier that never reached the signs page.
  try {
    localStorage.removeItem("cc-login-card");
  } catch {}
  // The street light board needs a street (not development rooms).
  $("#street-board-link").hidden = false;
  $<HTMLFormElement>("#add-house-form").addEventListener("submit", async (e) => {
    e.preventDefault();
    const input = $<HTMLInputElement>("#add-house-address");
    // "10, 35, 40" or one per line: added in that order along the street.
    const addresses = input.value.split(/[,\n]+/).map((a) => a.trim()).filter(Boolean);
    if (!addresses.length) return;
    if (await act("POST", "/api/admin/houses", { addresses }, `${addresses.length} house${addresses.length === 1 ? "" : "s"} added`)) {
      input.value = "";
    }
  });
  $("#street-name-save").addEventListener("click", () => {
    const name = $<HTMLInputElement>("#street-name-input").value.trim();
    if (name) void act("PATCH", "/api/admin/street", { name }, "Street name saved");
  });
  $("#street-url-save").addEventListener("click", () => {
    const publicUrl = $<HTMLInputElement>("#street-url-input").value.trim();
    if (publicUrl) void act("PATCH", "/api/admin/street", { publicUrl }, "Site address saved");
  });
  // Saved as soon as it's picked: the page refreshes every few seconds, which
  // would put the menu back to the saved look before a separate Save press.
  $("#street-season-input").addEventListener("change", async (e) => {
    const season = (e.target as HTMLSelectElement).value;
    // Reload so this page shows the new look too; every page is served in it from now on.
    if (await act("PATCH", "/api/admin/street", { season }, "Site look saved")) location.reload();
  });
  $("#pw-save").addEventListener("click", async () => {
    const current = $<HTMLInputElement>("#pw-current").value;
    const next = $<HTMLInputElement>("#pw-next").value;
    try {
      await api("PUT", "/api/admin/password", { current, next });
      toast("Password changed. Please sign in again.");
      setTimeout(() => location.reload(), 1500);
    } catch (e) {
      toast((e as Error).message);
    }
  });
  void refresh(true);
  setInterval(() => void refresh(), 5000);
}
